/* eslint-disable max-lines */
import { PropTypes, type ValidationMap } from '@elliemae/ds-props-helpers';
import type React from 'react';

// SSN Mask - Zip Code Mask
export declare namespace UseMaskT {
  export interface RequiredProps {
    valueSetter: React.Dispatch<React.SetStateAction<string>>;
  }

  export interface DefaultProps {
    onChange: (e: React.ChangeEvent<HTMLInputElement>, mask?: string) => void;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
    onBlur: React.FocusEventHandler<HTMLInputElement>;
  }

  export interface OptionalProps {}

  export interface Props extends Partial<DefaultProps>, OptionalProps, RequiredProps {}

  export interface InternalProps extends DefaultProps, OptionalProps, RequiredProps {}

  export interface OutputT {
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  }
}

export const useMaskDefaultProps: UseMaskT.DefaultProps = {
  onChange: () => null,
  onKeyDown: () => null,
  onBlur: () => null,
};

export const useMaskPropTypes = {
  valueSetter: PropTypes.func.description('React set state function for the value').isRequired,
  onChange: PropTypes.func.description('Callback for onChange'),
  onKeyDown: PropTypes.func.description('Callback for onKeyDown'),
} as ValidationMap<unknown>;

// Number Mask
export declare namespace UseNumberMaskT {
  export interface RequiredProps extends UseMaskT.RequiredProps {}

  export interface DefaultProps extends UseMaskT.DefaultProps {
    suffix: string;
    prefix: string;
    decimalPlaces: number;
    allowNegative: boolean;
    includeThousandsSeparator: boolean;
    decimalRequired: boolean;
  }

  export interface OptionalProps {
    max?: number;
    min?: number;
  }

  export interface Props extends Partial<DefaultProps>, OptionalProps, RequiredProps {}

  export interface InternalProps extends DefaultProps, OptionalProps, RequiredProps {}

  export interface OutputT extends UseMaskT.OutputT {
    onBlur: React.FocusEventHandler<HTMLInputElement>;
  }
}

export const useNumberMaskDefaultProps: UseNumberMaskT.DefaultProps = {
  ...useMaskDefaultProps,
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
};

export const useNumberMaskPropTypes = {
  ...useMaskPropTypes,
  suffix: PropTypes.string.description('String will be added at the end of the input value').defaultValue(''),
  prefix: PropTypes.string.description('String will be added at the start of the input value').defaultValue(''),
  decimalPlaces: PropTypes.number.description('Number of decimals allowed').defaultValue(2),
  allowNegative: PropTypes.bool.description('Wether the input allows negative number').defaultValue(false),
  includeThousandsSeparator: PropTypes.bool
    .description('Wether the integer part will be separated by thousands')
    .defaultValue(true),
  decimalRequired: PropTypes.bool.description('Wether decimal part is mandatory').defaultValue(false),
  max: PropTypes.number.description('Maximum value allowed'),
  min: PropTypes.number.description('Minimum value allowed'),
} as ValidationMap<unknown>;

// Phone Mask
export declare namespace UsePhoneMaskT {
  export interface RequiredProps extends UseMaskT.RequiredProps {}

  export interface DefaultProps extends UseMaskT.DefaultProps {
    isInternational: boolean;
  }

  export interface OptionalProps {}

  export interface Props extends Partial<DefaultProps>, OptionalProps, RequiredProps {}

  export interface InternalProps extends DefaultProps, OptionalProps, RequiredProps {}

  export interface OutputT extends UseMaskT.OutputT {}
}

export const usePhoneMaskDefaultProps: UsePhoneMaskT.DefaultProps = {
  ...useMaskDefaultProps,
  isInternational: false,
};

export const usePhoneMaskPropTypes = {
  ...useMaskPropTypes,
  isInternational: PropTypes.bool.description('Use or not internaltional phone mask number').defaultValue(false),
} as ValidationMap<unknown>;

// RegExp Mask
export declare namespace UseRegExpMaskT {
  export interface RequiredProps extends UseMaskT.RequiredProps {
    regExp: (string | RegExp)[];
  }

  export interface DefaultProps extends UseMaskT.DefaultProps {
    filledMask: boolean;
  }

  export interface OptionalProps {}

  export interface Props extends Partial<DefaultProps>, OptionalProps, RequiredProps {}

  export interface InternalProps extends DefaultProps, OptionalProps, RequiredProps {}

  export interface OutputT extends UseMaskT.OutputT {
    onBlur: React.FocusEventHandler<HTMLInputElement>;
  }
}

export const useRegExpMaskDefaultProps: UseRegExpMaskT.DefaultProps = {
  ...useMaskDefaultProps,
  filledMask: false,
};

export const useRegExpMaskPropTypes = {
  ...useMaskPropTypes,
  regExp: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)])).description(
    'Array with regexp or special chars per character',
  ).isRequired,
  filledMask: PropTypes.bool.description('Show blank chars positions').defaultValue(false),
} as ValidationMap<unknown>;

// ---------- Legacy props ----------
export declare namespace DSMaskT {
  export interface HookArgs {
    valueSetter: React.Dispatch<React.SetStateAction<string>>;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, mask?: string) => void;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
  }

  export interface HookReturn {
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
  }

  export interface NumberHookArgs extends HookArgs {
    suffix?: string;
    prefix?: string;
    decimalPlaces?: number;
    allowNegative?: boolean;
    includeThousandsSeparator?: boolean;
    decimalRequired?: boolean;
    max?: number;
    min?: number;
  }

  export interface PhoneHookArgs extends HookArgs {
    isInternational?: boolean;
  }

  export interface RegExpHookArgs extends HookArgs {
    filledMask: boolean;
    regExp: string[];
  }

  export type Hook = ((args: HookArgs) => HookReturn) & { displayName: string };
  export type PhoneHook = ((args: PhoneHookArgs) => HookReturn) & { displayName: string };
  export type NumberHook = ((args: NumberHookArgs) => HookReturn) & { displayName: string };
  export type RegExpHook = ((args: RegExpHookArgs) => HookReturn) & { displayName: string };
}

export const DSMaskHookDefaultProps: Partial<DSMaskT.HookArgs> = {
  onChange: () => null,
  onKeyDown: () => null,
  onBlur: () => null,
};

export const DSMaskPhoneHookDefaultProps: Partial<DSMaskT.PhoneHookArgs> = {
  ...DSMaskHookDefaultProps,
  isInternational: false,
};
export const DSMaskRegExpHookDefaultProps: Partial<DSMaskT.RegExpHookArgs> = {
  ...DSMaskHookDefaultProps,
  filledMask: false,
  regExp: [],
};

export const DSMaskSSNDefaultProps: Partial<DSMaskT.PhoneHookArgs> = {
  ...DSMaskHookDefaultProps,
};

export const DSMaskNumberHookDefaultProps: Partial<DSMaskT.NumberHookArgs> = {
  ...DSMaskHookDefaultProps,
  suffix: '',
  prefix: '',
  decimalPlaces: 2,
  allowNegative: false,
  includeThousandsSeparator: true,
  decimalRequired: false,
};

export const DSMaskHookPropsTypes = {
  valueSetter: PropTypes.func.description('React set state function for the value').isRequired,
  onChange: PropTypes.func.description('Callback for onChange'),
  onKeyDown: PropTypes.func.description('Callback for onKeyDown'),
} as ValidationMap<unknown>;

export const DSMaskRegExpHookPropsTypes = {
  ...DSMaskHookPropsTypes,
  regExp: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)])).description(
    'Array with regexp or special chars per character',
  ).isRequired,
  filledMask: PropTypes.bool.description('Show blank chars positions').defaultValue(false),
} as ValidationMap<unknown>;

export const DSMaskPhoneHookPropsTypes = {
  ...DSMaskHookPropsTypes,
  isInternational: PropTypes.bool.description('Use or not internaltional phone mask number').defaultValue(false),
} as ValidationMap<unknown>;

export const DSMaskNumberHookPropsTypes = {
  ...DSMaskHookPropsTypes,
  suffix: PropTypes.string.description('String will be added at the end of the input value').defaultValue(''),
  prefix: PropTypes.string.description('String will be added at the start of the input value').defaultValue(''),
  decimalPlaces: PropTypes.number.description('Number of decimals allowed').defaultValue(2),
  allowNegative: PropTypes.bool.description('Wether the input allows negative number').defaultValue(false),
  includeThousandsSeparator: PropTypes.bool
    .description('Wether the integer part will be separated by thousands')
    .defaultValue(true),
  decimalRequired: PropTypes.bool.description('Wether decimal part is mandatory').defaultValue(false),
} as ValidationMap<unknown>;
